import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import HoursInput from 'components/forms/inputs/rhf-inputs/HoursInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput.tsx';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { mustNotExceed } from 'components/forms/inputs/rhf-validators/validators';
import InfoTooltip from 'components/InfoTooltip';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Alert } from 'components/ui/alert';
import { FeatureFlag } from 'types/features.ts';
import { RemunerationCalcType } from 'types/matters.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { MultiplierToggle } from '../form/MultiplierToggle';
import TimecardCostBreakdown from '../form/TimecardCostBreakdown';
import { TimecardFormState } from './useTimecardForm';

/** A status banner above or inside the form: a glyph column, then a semibold message. */
export const FORM_ALERT_CLASS = 'text-fg flex items-center gap-2 rounded-lg py-2 pr-4 pl-3 font-semibold';

/** Billability, the used and billed hours with the uplift that explains them, remuneration and the multiplier. */
export const TimeBillingFields = ({ form }: { form: TimecardFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        isNewInvoiceTimecard,
        onIsNonBillableChanged,
        effectiveIsNonBillable,
        isNonBillable,
        hideDontNullifyOption,
        onPreserveNonBillableHoursChanged,
        totalTimeValidate,
        onTotalTimeChange,
        onTotalTimeBlur,
        effectiveTimecardIncrement,
        canEditBilledTime,
        billedTimeCapInHours,
        onBilledTimeChange,
        showBillingUpliftFields,
        billingUpliftValueFactorOptions,
        costAuthors,
        billedTimeInHours,
        multiplierRate,
        currentMatter,
        timecardDate,
        showRemunerationCalcTypeRadioInput,
        remunerationCalcTypeOptions,
        isUserPrivilegedToUpdateRemunerationTime,
        remunerationCalcType,
        onRemunerationTimeChange,
        multiplierTypeCode,
        multiplierTypes,
        handleMultiplierTypeCode,
    } = form;
    // On the record page the yes/no fields and the two times are each a cell of the card's grid, where the
    // read view has its row.
    const inSection = useInRecordSection();

    return (
        <>
            {isNewInvoiceTimecard ? null : (
                <div className={inSection ? 'contents' : 'flex flex-row gap-4'}>
                    <CheckboxInput
                        control={control}
                        name="isNonBillable"
                        label="timecards.form.is-non-billable"
                        rightLabelComponent={<InfoTooltip text={'timecards.tooltips.is-non-billable'} />}
                        onChange={onIsNonBillableChanged}
                        disabled={effectiveIsNonBillable}
                    />
                    {isNonBillable && !hideDontNullifyOption ? (
                        <CheckboxInput
                            control={control}
                            name="preserveNonBillableHours"
                            label="timecards.preserve-non-billable-hours"
                            onChange={onPreserveNonBillableHoursChanged}
                            disabled={effectiveIsNonBillable}
                            rightLabelComponent={
                                <InfoTooltip text={'timecards.tooltips.preserve-non-billable-hours'} />
                            }
                        />
                    ) : null}
                </div>
            )}
            <div className={inSection ? 'contents' : 'flex gap-4'}>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <HoursInput
                        control={control}
                        name="totalTimeInHours"
                        label="timecards.form.total-time"
                        suffixComponent="h"
                        required
                        validate={totalTimeValidate}
                        onChange={onTotalTimeChange}
                        onBlur={onTotalTimeBlur}
                        useIncrementDecrementButtons
                        setValue={setValue}
                        incrementStep={effectiveTimecardIncrement}
                        showMinutesHint
                    />
                </div>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <HoursInput
                        control={control}
                        name="billedTimeInHours"
                        label="timecards.form.billed-time"
                        suffixComponent="h"
                        disabled={!canEditBilledTime}
                        maxValue={billedTimeCapInHours}
                        validate={
                            billedTimeCapInHours === undefined
                                ? undefined
                                : mustNotExceed(
                                      billedTimeCapInHours,
                                      t('validation.cannot-exceed-hours', {
                                          max: String(billedTimeCapInHours),
                                      })
                                  )
                        }
                        onChange={(event) => onBilledTimeChange(event.target.value)}
                        incrementStep={effectiveTimecardIncrement}
                    />
                </div>
            </div>
            {/* One outlined panel from the warning down through Remuneration time. The
                                    wrapper carries its own gap-4 in BOTH branches: it is a single child of
                                    the section's field column, so without it the elements it encloses would
                                    lose the 16px gap that column puts between its children. */}
            <div
                className={cn(
                    'flex flex-col gap-4',
                    showBillingUpliftFields && 'border-info rounded-lg border-[1.5px] p-4'
                )}
            >
                {showBillingUpliftFields && (
                    <>
                        <Alert className={cn(FORM_ALERT_CLASS, 'border-info bg-info-lighter')}>
                            <span className="flex w-6 shrink-0 justify-center">
                                <InfoOutlinedIcon className="text-info size-4" />
                            </span>
                            <div className="min-w-0">{t('timecards.form.billing-uplift-warning')}</div>
                        </Alert>
                        <MultiSelectInput
                            control={control}
                            setValue={setValue}
                            name="billingUpliftValueFactors"
                            label="timecards.form.billing-uplift-value-factors"
                            options={billingUpliftValueFactorOptions}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            rightLabelComponent={
                                <InfoTooltip text={'timecards.tooltips.billing-uplift-value-factors'} />
                            }
                        />
                        <TextAreaInput
                            control={control}
                            name="valueFactorsNotes"
                            label="timecards.form.value-factors-notes"
                            maxLength={5000}
                            enableAutofill
                        />
                    </>
                )}
                <TimecardCostBreakdown
                    authors={costAuthors}
                    billedTimeInHours={billedTimeInHours}
                    multiplierRate={multiplierRate}
                    isNonBillable={isNonBillable}
                    matterId={currentMatter?.id}
                    timecardDate={timecardDate}
                    currency={currentMatter?.pricelistCurrencyTypeCode}
                />
                {showRemunerationCalcTypeRadioInput && (
                    <div className="flex gap-4">
                        <div className="flex-[0.5]">
                            <RadioInput
                                setValue={setValue}
                                control={control}
                                label="timecards.remuneration-time"
                                name="remunerationCalcType"
                                options={remunerationCalcTypeOptions}
                                getOptionLabel="label"
                                getOptionIdentifier="value"
                                disabled={!isUserPrivilegedToUpdateRemunerationTime}
                                // Beside the hours field in this panel, on the record page too.
                                row={false}
                            />
                        </div>
                        <div className="flex-[0.2] self-end">
                            {remunerationCalcType === RemunerationCalcType.MANUAL_ENTRY ? (
                                <HoursInput
                                    control={control}
                                    name="remunerationTimeInHours"
                                    onChange={onRemunerationTimeChange}
                                    suffixComponent="h"
                                    disabled={!isUserPrivilegedToUpdateRemunerationTime}
                                />
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
            <FeatureFlagged action="show" flag={FeatureFlag.TC_MULTIPLIER}>
                <div className="flex flex-col">
                    <p className="text-body2 text-fg-secondary my-2">{t('timecards.form.multiplier-code')}</p>
                    <MultiplierToggle
                        value={multiplierTypeCode}
                        options={multiplierTypes}
                        onChange={handleMultiplierTypeCode}
                    />
                </div>
            </FeatureFlagged>
        </>
    );
};
